import { useState } from 'react';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { Textarea } from '../../components/Field';
import { Card, Page } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { useCanWrite } from '../../account/useCanWrite';
import { deleteNote, saveNote, useNotes } from '../../data/notes';

/** More → Notes: a simple journal, and questions to take to the doctor (they go in the doctor report). */
export function NotesPage() {
  const db = useDb();
  const canWrite = useCanWrite();
  const { notes } = useNotes();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    if (!body.trim()) return;
    try {
      await saveNote(db, undefined, { noted_at: new Date().toISOString(), body: body.trim().slice(0, 4000) });
      setBody('');
      setError(null);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    }
  }

  return (
    <Page title="Notes & questions" back="/more">
      {canWrite ? (
        <Card>
          <label htmlFor="new-note" className="mb-1 block text-sm font-medium">
            New note
          </label>
          <Textarea id="new-note" value={body} onChange={(event) => setBody(event.target.value)} placeholder="How you’re feeling, or a question for your doctor" />
          {error ? <p className="mt-1 text-sm text-clinical-danger">{error}</p> : null}
          <Button className="mt-2 w-full" disabled={!body.trim()} onClick={() => void add()}>
            Save note
          </Button>
        </Card>
      ) : null}
      {notes.length ? (
        <ul className="space-y-2">
          {notes.map((note) => (
            <li key={note.id} className="rounded-xl border border-clinical-line bg-clinical-card p-4 shadow-soft">
              <div className="mb-1 flex items-center gap-2">
                <span className="flex-1 text-xs text-clinical-muted">{format(new Date(note.noted_at), 'EEE d MMM yyyy, HH:mm')}</span>
                {canWrite ? (
                  <button type="button" aria-label="Delete note" onClick={() => void deleteNote(db, note.id)} className="focus-ring rounded p-1 text-clinical-muted hover:text-clinical-danger">
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <p className="whitespace-pre-line text-sm">{note.body}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-1 text-sm text-clinical-muted">No notes yet. Notes from the chosen dates are included in the doctor report.</p>
      )}
    </Page>
  );
}
